import { Injectable } from '@angular/core';
import { RequestService } from '@app/core/request';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

/** Laboratorio WXRL obligatorio ausente o inválido (no entero > 0). */
export class WxrlMissingLabNrError extends Error {
  constructor() {
    super('WXRL.ERRORS.LAB_NR_REQUIRED');
    this.name = 'WxrlMissingLabNrError';
    Object.setPrototypeOf(this, WxrlMissingLabNrError.prototype);
  }
}

/** Valida `labNr` para endpoints por laboratorio; lanza {@link WxrlMissingLabNrError} si no aplica. */
export function assertWxrlLabNr(labNr: unknown): number {
  const n =
    typeof labNr === 'string' && String(labNr).trim() !== ''
      ? Number(String(labNr).trim())
      : Number(labNr);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
    throw new WxrlMissingLabNrError();
  }
  return n;
}

export type WxrlAuthor = {
  PersNr: number;
  Name: string;
  Kuerzel: string;
  Kennwort: string;
  KurzName: string;
  SignCode: string;
  Sicherheit: number;
  ExtPersID: string;
  PersonalInaktiv: string;
  KWGueltigBis: string;
  Kennwort1: string;
  Kennwort2: string;
  Kennwort3: string;
  Kennwort4: string;
  Kennwort5: string;
};


export type WxrlOperator = {
  posR: number;
  idOperator: string;
  /** Clave estable para el dropdown (idOperator|posR); evita colisiones si PosR se repite en catálogo */
  operatorKey?: string;
  labelOperator: string;
  /** RULESCATALOGOPERATOR.ROperator; treetable / SP bloque operador */
  catalogROperator?: string;
  info: string;
  enableV1: number;
  multiSelectV1: number;
  enableV2: number;
  multiSelectV2: number;
};

export type WxrlOption = {
  value: string | number;
  label: string;
};

export type WxrlActionCatalog = {
  actionKrz: string;
  labelAction: string;
  info: string;
};

export type WxrlActionCatalogDetail = {
  actionKrz: string;
  labelAction: string;
  enabledMethNr: number; // 1 o 0
  enabledGrupNr: number;
  enabledTextNr: number;
  enabledActionValue: number;
  enabledAdditional1: number;
  enabledAdditional2: number;
  add1Table: number; // 1 = dropdown, 0 = textbox
  add2Table: number;
  labelAdditional1: string;
  labelAdditional2: string;
  qryAdd1?: string;
  qryAdd2?: string;
};

export type WxrlActionState = {
  schl: string;
  schluessel: string;
};

export type WxrlActionTest = {
  value: number;
  label: string;
};

export type WxrlActionText = {
  value: number;
  label: string;
};

export type WxrlActionPersonnel = {
  value: number;
  label: string;
};

export type WxrlActionHeader = {
  ruleActionNr?: number;
  ruleActionDesc?: string;
  labNr?: number;
  createdWhen?: string;
  author?: WxrlAuthor;
  info?: string;
};

export type WxrlRelatedRule = {
  ruleNr: number;
  descripcion: string;
};

export type WxrlRuleAction = {
  ruleActionNr: number;
  descripcion: string;
};

export type WxrlRuleActionDetail = {
  actionHeader: WxrlActionHeader;
  actions: WxrlAction[];
};

export type WxrlRuleListObject = {
  subjectType: string;
  subjectID: string;
  subjectName: string;
  ruleNr: number;
  ruleName: string;
  formula: string;
  negRule: string;
  countEval: number;
  percentExec: number;
  actionHeader?: WxrlActionHeader;
};

export type WxrlRuleDetail = {
  ruleNr: number;
  ruleName: string;
  ruleKrz: string;
  condition: string;
  negRule: boolean;
  ruleActionNr: number;
  kommentar: string;
  author: number | WxrlAuthor;
  createdWhen: string;
  modified: number | WxrlAuthor;
  modWHen: string;
  execCount: number;
  execCountAction: number;
  dateCount: string;
  resume: string;
  conditions: WxrlCondition[];
  actions: WxrlAction[];
  triggers: WxrlRuleTrigger[];
  actionHeader?: WxrlActionHeader;
  /** Número de laboratorio; el backend lo devuelve a nivel de regla cuando hay actionHeader */
  labNr?: number;
  /** Tipo de laboratorio: LABC, LABM o LABG. Controla campos específicos como Aislamiento. */
  labType?: string;
};

// WxrlRuleDetailDto is the same as WxrlRuleDetail but negRule is string of 'S' or 'N'
export type WxrlRuleDetailDto = Omit<WxrlRuleDetail, 'negRule'> & {
  negRule: 'S' | 'N';
};

export type WxrlCloneRuleRequest = {
  newRuleNr: number;
  triggerType?: string;
  triggerElement?: number;
};

export type WxrlCondition = {
  condRule: string;
  posC: number;
  kommentar: string;
  condId: string;
  /** Id del operador en catálogo (RULESCATALOGCONDITIONDEF); devuelto al cargar la regla */
  idOperator?: string;
  saveValue: string;
  methName: string;
  grupNr: number;
  /** PosR en RULESCONDITION / catálogo (API lo expone como rOperator por histórico). */
  rOperator: string;
  /** RULESCATALOGOPERATOR.ROperator; misma columna que usa wspi_XRules_TreeTable para el bloque de operador. */
  catalogROperator?: string;
  labelOperator: string;
  entryValue: string;
  negCond: string;
  exitCond: string;
  visible: string;
  isolate: string;
  tipoRes: string;
  /** RULESCONDITION.DebugCond; no UI, debe persistir al actualizar (#550) */
  debugCond?: number | null;
  /** RULESCONDITION.Relation char(4); no UI, debe persistir al actualizar (#549) */
  relation?: string | null;
};

export type WxrlAction = {
  ruleState: string;
  posA: number;
  ruleActionDesc: string;
  actionKrz: string;
  /** RULESCATALOGACTION.LabelAction si el backend hace JOIN al cargar la regla. */
  labelAction?: string;
  methNr: number;
  grupNr: number;
  textNr: number;
  actionValue: string;
  additional1: string;
  additional2: string;
  persNr: number;
  kommentar: string;
};

export type WxrlRuleTrigger = {
  subjectType: string;
  subjectTypeDescription?: string;
  subjectID: number;
  subjectName: string;
  methNrFilter: string;
  alarmFilter: string;
  posR: number;
  ruleState: string;
  ruleStateDescription?: string;
  triggEvent: string;
  triggEventDescription?: string;
  auftrNrTest: string;
  debug: number;
  kommentar: string;
};

export type WxrlArea = {
  labBerNr: number;
  labBerName: string;
  labNr: number;
};

export type WxrlLaboratory = {
  labNr: number;
  labName: string;
  labType?: string;
  applName: string;
  areas: WxrlArea[];
};

export type WxrlRuleState = {
  codRuleState: string;
  ruleState: string;
};

export type WxrlSubject = {
  value: number;
  label: string;
};

export type WxrlSubjectType = {
  codSubjectType: string;
  subjectType: string;
};

export type WxrlMode = 'Desencadenante' | 'Contiene';

export type WxrlProfile = {
  grupNr: number;
  grupName: string;
};

@Injectable({
  providedIn: 'root',
})
export class WrxlRulesService {
  /**
   *
   */
  constructor(
    private request: RequestService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  async listRules(
    labNr: number,
    ruleState?: string,
    subjectType?: string,
    subjectID?: number | string,
    mode?: string,
    labBerNr?: number
  ): Promise<WxrlRuleListObject[]> {
    const params = new URLSearchParams();
    params.append('labNr', labNr.toString());
    if (ruleState) {
      params.append('ruleState', ruleState);
    }
    if (subjectType) {
      params.append('subjectType', subjectType);
    }
    if (subjectID) {
      params.append('subjectID', subjectID.toString());
    }
    if (mode) {
      params.append('mode', mode);
    }
    if (labBerNr) {
      params.append('labBerNr', labBerNr.toString());
    }
    const result = await firstValueFrom(
      this.request.get<WxrlRuleListObject[]>(
        `/api/wxrl/rules?${params.toString()}`
      )
    );

    return result;
  }

  async getRule(ruleID: string): Promise<WxrlRuleDetail> {
    const result = await firstValueFrom(
      this.request.get<WxrlRuleDetailDto>(`/api/wxrl/rules/${ruleID}`)
    );
    // Convert negRule from 'S'/'N' to boolean
    const ruleDetail: WxrlRuleDetail = {
      ...result,
      negRule: result.negRule === 'S' ? true : false,
    };
    return ruleDetail;
  }

  /**
   * Comprueba si el usuario actual tiene acceso a WXRL (existe en base reglas/laboratorios).
   * Útil para mostrar mensaje de no autorizado o ocultar menú sin depender del fallo de otra petición.
   */
  async checkWxrlAccess(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.request.get<{ hasAccess: boolean }>('/api/wxrl/access')
      );
      return true;
    } catch (error: any) {
      const status = error?.status ?? error?.statusCode;
      const msg = error?.error?.message ?? error?.message ?? '';
      if (status === 401 || (typeof msg === 'string' && /not found in database|Username.*not found/i.test(msg))) {
        return false;
      }
      throw error;
    }
  }

  async getLaboratories(): Promise<WxrlLaboratory[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlLaboratory[]>('/api/wxrl/laboratories')
    );
    return result;
  }

  async getStatuses(): Promise<WxrlRuleState[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlRuleState[]>('/api/wxrl/statuses')
    );
    return result;
  }

  async getSubjectTypes(): Promise<WxrlSubjectType[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlSubjectType[]>('/api/wxrl/subject-types')
    );
    return result;
  }

  async getTipoRes(): Promise<{ schl: string; schluessel: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ schl: string; schluessel: string }[]>('/api/wxrl/tipo-res')
    );
    return result;
  }

  async getCatalogConditions(): Promise<{ condId: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ condId: string; label: string }[]>('/api/wxrl/catalog-conditions')
    );
    return result;
  }

  async getOperatorsByCondId(condId: string): Promise<WxrlOperator[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlOperator[]>(`/api/wxrl/operators/${condId.toLowerCase()}`)
    );
    return result;
  }

  async getElementoOptions(
    idOperator: string,
    condId?: string | null,
    labNr?: number | string | null
  ): Promise<WxrlOption[]> {
    const enc = encodeURIComponent(idOperator);
    const params: Record<string, string> = {};
    if (condId != null && String(condId).trim() !== '') {
      params['condId'] = String(condId).trim();
    }
    if (labNr != null && String(labNr).trim() !== '') {
      params['labNr'] = String(labNr).trim();
    }
    const opts = Object.keys(params).length > 0 ? params : undefined;
    const result = await firstValueFrom(
      this.request.get<WxrlOption[]>(`/api/wxrl/elemento-options/${enc}`, opts)
    );
    return result;
  }

  async getEntryValueOptions(
    idOperator: string,
    condId?: string | null,
    labNr?: number | string | null
  ): Promise<WxrlOption[]> {
    const enc = encodeURIComponent(idOperator);
    const params: Record<string, string> = {};
    if (condId != null && String(condId).trim() !== '') {
      params['condId'] = String(condId).trim();
    }
    if (labNr != null && String(labNr).trim() !== '') {
      params['labNr'] = String(labNr).trim();
    }
    const opts = Object.keys(params).length > 0 ? params : undefined;
    const result = await firstValueFrom(
      this.request.get<WxrlOption[]>(`/api/wxrl/entry-value-options/${enc}`, opts)
    );
    return result;
  }

  async getPerfiles(labNr: number): Promise<WxrlProfile[]> {
    const n = assertWxrlLabNr(labNr);
    const result = await firstValueFrom(
      this.request.get<WxrlProfile[]>(`/api/wxrl/perfiles/${n}`)
    );
    return result;
  }

  async getRuleActions(labNr: number): Promise<WxrlRuleAction[]> {
    const n = assertWxrlLabNr(labNr);
    const result = await firstValueFrom(
      this.request.get<WxrlRuleAction[]>(`/api/wxrl/rule-actions/${n}`)
    );
    return result;
  }

  async getRuleActionDetail(ruleActionNr: number): Promise<WxrlRuleActionDetail> {
    const result = await firstValueFrom(
      this.request.get<WxrlRuleActionDetail>(`/api/wxrl/rule-actions/detail/${ruleActionNr}`)
    );
    return result;
  }

  // ?labNr=1&labBerNr=10&ruleState=RXAC
  // subjectType ahora es el código (ej: RXRL, RXPR, etc.)
  async getSubjects(
    subjectType: string,
    labNr: number,
    labBerNr?: number,
    ruleState?: string
  ): Promise<WxrlSubject[]> {
    const params = new URLSearchParams();
    params.append('subjectType', subjectType);
    params.append('labNr', labNr.toString());
    if (labBerNr) {
      params.append('labBerNr', labBerNr.toString());
    }
    if (ruleState) {
      params.append('ruleState', ruleState);
    }

    const result = await firstValueFrom(
      this.request.get<WxrlSubject[]>(`/api/wxrl/subjects?${params.toString()}`)
    );
    return result.map((subject) => ({
      value: subject.value,
      // RXPR (#696): el API ya devuelve `MethNr (tipo) MethName`; no duplicar `(MethNr)`.
      label:
        subjectType === 'RXPR'
          ? subject.label
          : ` (${subject.value}) ${subject.label}`,
    }));
  }

  async createRule(payload: WxrlRuleDetail): Promise<WxrlRuleDetail> {
    const dtoPayload: WxrlRuleDetailDto = {
      ...payload,
      negRule: payload.negRule ? 'S' : 'N',
    };
    const result = await firstValueFrom(
      this.request.post<WxrlRuleDetailDto>('/api/wxrl/rules', dtoPayload)
    );
    return {
      ...result,
      negRule: result.negRule === 'S',
    };
  }

  async updateRule(
    ruleID: number | string,
    payload: WxrlRuleDetail
  ): Promise<WxrlRuleDetail> {
    const dtoPayload: WxrlRuleDetailDto = {
      ...payload,
      negRule: payload.negRule ? 'S' : 'N',
    };
    const result = await firstValueFrom(
      this.request.put<WxrlRuleDetailDto>(`/api/wxrl/rules/${ruleID}`, dtoPayload)
    );
    return {
      ...result,
      negRule: result.negRule === 'S',
    };
  }

  /** Reset de contadores y fecha «Desde» en RULES (#543). Requiere permiso de edición en el laboratorio. */
  async resetRuleCounters(ruleID: number | string, labNr: number): Promise<WxrlRuleDetail> {
    const result = (await firstValueFrom(
      this.request.post(`/api/wxrl/rules/${ruleID}/reset-counters`, { labNr })
    )) as WxrlRuleDetailDto;
    return {
      ...result,
      negRule: result.negRule === 'S',
    };
  }

  /**
   * Activa o desactiva todos los disparadores de la regla (wspi_XRules_ActiveInactive, #703).
   */
  async setRuleTriggersActiveInactive(
    ruleID: number | string,
    labNr: number,
    action: 'Enable' | 'Disable'
  ): Promise<{ success: boolean }> {
    return (await firstValueFrom(
      this.request.post(`/api/wxrl/rules/${ruleID}/triggers/active-inactive`, {
        labNr,
        action,
      })
    )) as { success: boolean };
  }

  async getMaxRuleNr(): Promise<{ maxRuleNr: number }> {
    const result = await firstValueFrom(
      this.request.get<{ maxRuleNr: number }>('/api/wxrl/rules/max-rule-nr')
    );
    return result;
  }

  async checkCreatePermission(labNr: number): Promise<{ hasPermission: boolean }> {
    const result = await firstValueFrom(
      this.request.get<{ hasPermission: boolean }>(`/api/wxrl/permissions/${labNr}`)
    );
    return result;
  }

  async getNextConditionCode(ruleNr: number): Promise<{ nextConditionCode: string }> {
    const result = await firstValueFrom(
      this.request.get<{ nextConditionCode: string }>(`/api/wxrl/rules/${ruleNr}/next-condition-code`)
    );
    return result;
  }

  async ruleExists(ruleNr: number): Promise<boolean> {
    try {
      await firstValueFrom(
        this.request.get(`/api/wxrl/rules/${ruleNr}/exists`)
      );
      return true;
    } catch (error: any) {
      if (error?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  async cloneRule(
    sourceRuleNr: number,
    newRuleNr: number,
    triggerType?: string,
    triggerElement?: number
  ): Promise<WxrlRuleDetail> {
    const cloneRequest: WxrlCloneRuleRequest = {
      newRuleNr,
      triggerType,
      triggerElement,
    };
    const result = await firstValueFrom(
      this.request.post(`/api/wxrl/rules/${sourceRuleNr}/clone`, cloneRequest) as any
    ) as WxrlRuleDetailDto;
    return {
      ...result,
      negRule: result.negRule === 'S',
    };
  }

  async getActionCatalog(labNr: number): Promise<WxrlActionCatalog[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionCatalog[]>(`/api/wxrl/action-catalog/${labNr}`)
    );
    return result;
  }

  async getActionCatalogDetail(actionKrz: string): Promise<WxrlActionCatalogDetail> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionCatalogDetail>(`/api/wxrl/action-catalog-detail/${actionKrz}`)
    );
    return result;
  }

  async getActionStates(): Promise<WxrlActionState[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionState[]>(`/api/wxrl/action-states`)
    );
    return result;
  }

  async getActionTests(labNr: number): Promise<WxrlActionTest[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionTest[]>(`/api/wxrl/action-tests/${labNr}`)
    );
    return result;
  }

  async getActionTexts(labNr: number): Promise<WxrlActionText[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionText[]>(`/api/wxrl/action-texts/${labNr}`)
    );
    return result;
  }

  async getActionPersonnel(): Promise<WxrlActionPersonnel[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionPersonnel[]>(`/api/wxrl/action-personnel`)
    );
    return result;
  }

  async getActionIsolation(): Promise<WxrlActionState[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlActionState[]>(`/api/wxrl/action-isolation`)
    );
    return result;
  }

  async getActionAdditional1Options(actionKrz: string, labNr: number): Promise<WxrlOption[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlOption[]>(`/api/wxrl/action-additional1/${actionKrz}/${labNr}`)
    );
    return result;
  }

  async getActionAdditional2Options(actionKrz: string, labNr: number): Promise<WxrlOption[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlOption[]>(`/api/wxrl/action-additional2/${actionKrz}/${labNr}`)
    );
    return result;
  }

  async getRelatedRules(ruleActionNr: number): Promise<WxrlRelatedRule[]> {
    const result = await firstValueFrom(
      this.request.get<WxrlRelatedRule[]>(`/api/wxrl/related-rules/${ruleActionNr}`)
    );
    return result;
  }

  // Métodos para desplegables de disparadores
  async getTriggerTypes(): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>('/api/wxrl/triggers/types')
    );
    return result;
  }

  async getTriggerStates(): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>('/api/wxrl/triggers/states')
    );
    return result;
  }

  async getTriggerTests(labNr: number): Promise<{ value: number; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: number; label: string }[]>(`/api/wxrl/triggers/tests/${labNr}`)
    );
    return result;
  }

  async getTriggerEvents(type: string): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>(`/api/wxrl/triggers/events/${type}`)
    );
    return result;
  }

  async getAlarmTypes(): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>('/api/wxrl/triggers/alarm-types')
    );
    return result;
  }

  async getAlarmActions(): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>('/api/wxrl/triggers/alarm-actions')
    );
    return result;
  }

  async getTextResults(labNr: number): Promise<{ value: number; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: number; label: string }[]>(`/api/wxrl/triggers/text-results/${labNr}`)
    );
    return result;
  }

  async getIsolationOptions(): Promise<{ value: string; label: string }[]> {
    const result = await firstValueFrom(
      this.request.get<{ value: string; label: string }[]>('/api/wxrl/triggers/isolation-options')
    );
    return result;
  }

  /**
   * Muestra error en toastr. Si el backend envía clave i18n en `message` y `messageParams`, se traduce.
   */
  handleError(error: any): void {
    console.error('Error in WxrlRulesService:', error);
    const raw =
      error?.error?.message ?? error?.message ?? error?.statusText ?? 'UNKNOWN_ERROR';
    const params = error?.error?.messageParams as Record<string, string> | undefined;
    const message = this.resolveApiErrorMessage(String(raw), params);
    this.toastr.error(message);
  }

  private resolveApiErrorMessage(raw: string, params?: Record<string, string>): string {
    if (this.looksLikeTranslationKey(raw)) {
      const translated = this.translate.instant(raw, params ?? {});
      if (translated !== raw) {
        return translated;
      }
    }
    return raw;
  }

  private looksLikeTranslationKey(s: string): boolean {
    return /^[A-Z][A-Za-z0-9_.]*$/.test(s) && s.includes('.');
  }
}
